import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Lock, Truck, RotateCcw, ChevronLeft } from "lucide-react";
import api, { productImage } from "@/lib/api";
import { useCart } from "@/lib/cart";
import StorefrontLayout from "@/components/StorefrontLayout";
import AIChat from "@/components/AIChat";

const PHYSICAL_CATEGORIES = ["Print", "Apparel", "Home", "Accessories"];

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`)
      .then((r) => setP(r.data))
      .catch((err) => { console.error(`load product ${id} failed`, err); setP(null); });
    api.get(`/products/${id}/related`)
      .then((r) => setRelated(r.data || []))
      .catch((err) => console.error(`load related for ${id} failed`, err));
  }, [id]);

  const isPhysical = p ? PHYSICAL_CATEGORIES.includes(p.category) : false;

  if (!p) return <StorefrontLayout><div className="max-w-[1320px] mx-auto px-6 py-24 font-mono-ui">Loading…</div></StorefrontLayout>;

  const handleAdd = () => add(p);

  return (
    <StorefrontLayout>
      <Helmet>
        <title>{`${p.title} — Meridian Press`}</title>
        <meta name="description" content={(p.description || "").slice(0, 180)} />
        <link rel="canonical" href={`${window.location.origin}/product/${p.id}`} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${p.title} — Meridian Press`} />
        <meta property="og:description" content={(p.description || "").slice(0, 180)} />
        <meta property="og:url" content={`${window.location.origin}/product/${p.id}`} />
        <meta property="og:image" content={productImage(p)} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${p.title} — Meridian Press`} />
        <meta name="twitter:description" content={(p.description || "").slice(0, 180)} />
        <meta name="twitter:image" content={productImage(p)} />
        <meta property="product:price:amount" content={String(p.price.toFixed(2))} />
        <meta property="product:price:currency" content="USD" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": p.title,
          "description": (p.description || "").slice(0, 500),
          "image": productImage(p),
          "sku": p.id,
          "brand": { "@type": "Brand", "name": "Meridian Press" },
          "category": p.category,
          "offers": {
            "@type": "Offer",
            "url": `${window.location.origin}/product/${p.id}`,
            "priceCurrency": "USD",
            "price": p.price.toFixed(2),
            "availability": "https://schema.org/InStock",
            "itemCondition": "https://schema.org/NewCondition",
          },
        })}</script>
      </Helmet>
      <section className="rule-b">
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-4">
          <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm hover:opacity-60">
            <ChevronLeft className="w-4 h-4" /> Back to collection
          </Link>
        </div>
      </section>

      <section className="max-w-[1320px] mx-auto px-6 sm:px-10 py-12 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="aspect-[4/5] overflow-hidden bx" style={{ background: "var(--paper-2)" }}>
            <img src={productImage(p)} alt={p.title} className="w-full h-full object-cover" />
          </div>
        </div>

        <div>
          <div className="label-mono mb-3" style={{ color: "var(--accent)" }}>
            {p.ai_generated ? "STUDIO EDITION" : "CURRENT EDITION"}
          </div>
          <h1 data-testid="product-title" className="font-display text-4xl sm:text-5xl font-medium tracking-tight">{p.title}</h1>
          <div data-testid="product-price" className="mt-4 font-mono-ui text-2xl">
            ${p.price.toFixed(2)}{" "}
            <span className="text-sm" style={{ color: "var(--ink-mute)" }}>USD</span>
          </div>

          <p data-testid="product-desc" className="mt-8 text-base leading-relaxed" style={{ color: "var(--ink-soft)" }}>{p.description}</p>

          {/* ─── Optional structured value blocks (rendered only if the product has them) ─── */}
          <ProductValueSections p={p} />

          <div className="mt-6 flex flex-wrap gap-2">
            {p.tags.map((t) => (
              <span key={t} className="px-3 py-1 rounded-full text-xs font-mono-ui" style={{ background: "var(--paper-2)" }}>#{t}</span>
            ))}
          </div>

          <div className="mt-8">
            <button data-testid="add-to-cart-btn" onClick={handleAdd} className="btn-pill w-full sm:w-auto justify-center">
              Add to bag — ${p.price.toFixed(2)}
            </button>
            <ul data-testid="buy-trust-icons" className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs" style={{ color: "var(--ink-soft)" }}>
              {isPhysical ? (
                <>
                  <li className="flex items-center gap-1.5"><span style={{ color: "var(--leaf)" }}>✓</span> Ships in 5-7 Days</li>
                  <li className="flex items-center gap-1.5"><span style={{ color: "var(--leaf)" }}>✓</span> 30-Day Returns</li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-1.5"><span style={{ color: "var(--leaf)" }}>✓</span> Instant Delivery</li>
                  <li className="flex items-center gap-1.5"><span style={{ color: "var(--leaf)" }}>✓</span> Lifetime Access</li>
                </>
              )}
              <li className="flex items-center gap-1.5"><span style={{ color: "var(--leaf)" }}>✓</span> Secure Stripe Checkout</li>
            </ul>
          </div>

          {/* ─── How it works — 3 clear steps ─── */}
          <div className="mt-10 rule-t pt-8" data-testid="how-it-works">
            <div className="label-mono mb-4" style={{ color: "var(--accent)" }}>/ HOW IT WORKS</div>
            <ol className="grid sm:grid-cols-3 gap-4">
              {[
                { n: "01", t: "Add to bag", d: "One tap, one clear price — no surprises at checkout." },
                { n: "02", t: "Secure Stripe checkout", d: "Encrypted, PCI-compliant. Apple Pay, cards, Link." },
                isPhysical
                  ? { n: "03", t: "Ships to your door", d: "Printed & shipped in 5-7 business days." }
                  : { n: "03", t: "Instant delivery", d: "Download link on-screen and in your inbox. Lifetime access." },
              ].map((s) => (
                <li key={s.n} className="bx p-4" style={{ background: "var(--paper-2)" }}>
                  <div className="font-mono-ui text-xs" style={{ color: "var(--accent)" }}>{s.n}</div>
                  <div className="font-display text-base font-semibold mt-1">{s.t}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>{s.d}</div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-12 rule-t pt-8 grid grid-cols-2 gap-6 text-sm">
            {isPhysical ? (
              <>
                <div className="flex gap-3">
                  <Truck className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
                  <div><div className="font-medium">Free shipping over $50</div><div style={{ color: "var(--ink-soft)" }}>USPS / DHL · 3-7 days</div></div>
                </div>
                <div className="flex gap-3">
                  <RotateCcw className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
                  <div><div className="font-medium">30-day returns</div><div style={{ color: "var(--ink-soft)" }}>No questions asked</div></div>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-3">
                  <Truck className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
                  <div><div className="font-medium">Instant delivery</div><div style={{ color: "var(--ink-soft)" }}>Download link on-screen &amp; in your inbox</div></div>
                </div>
                <div className="flex gap-3">
                  <RotateCcw className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
                  <div><div className="font-medium">Lifetime access</div><div style={{ color: "var(--ink-soft)" }}>Personal &amp; commercial use license</div></div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <AIChat />

      {/* ─── You may also like ─── */}
      {related.length > 0 && (
        <section className="rule-t" data-testid="related-section">
          <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-16">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="label-mono mb-2" style={{ color: "var(--accent)" }}>/ YOU MAY ALSO LIKE</div>
                <h2 className="font-display text-3xl font-medium tracking-tight">More from {p.category}.</h2>
              </div>
              <Link to={`/shop?category=${encodeURIComponent(p.category)}`} className="text-sm font-medium underline underline-offset-4 hover:opacity-60">
                See all →
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/product/${r.id}`} data-testid={`related-${r.id}`} className="product-card group block">
                  <div className="relative aspect-[4/5] overflow-hidden bx" style={{ background: "var(--paper-2)" }}>
                    <img src={productImage(r)} alt={r.title} className="w-full h-full object-cover transition duration-500 group-hover:scale-[1.04]" />
                  </div>
                  <div className="mt-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-base font-medium truncate">{r.title}</div>
                      <div className="label-mono mt-0.5">{r.category}</div>
                    </div>
                    <div className="font-mono-ui text-sm">${r.price.toFixed(2)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </StorefrontLayout>
  );
}

// ─── Optional value blocks: rendered only when the product doc has them ───
// (kept out of the main render tree so the file stays a single default export).
function ProductValueSections({ p }) {
  const problem = p.problem || "";
  const outcome = p.outcome || "";
  const included = Array.isArray(p.whats_included) ? p.whats_included : [];
  const audience = p.whos_it_for || "";
  const different = p.why_different || "";
  if (!problem && !outcome && !included.length && !audience && !different) return null;
  return (
    <div className="mt-8 grid gap-4" data-testid="product-value-blocks">
      {problem && (
        <div className="bx p-4" style={{ background: "var(--paper-2)" }} data-testid="value-problem">
          <div className="label-mono" style={{ color: "var(--accent)" }}>/ THE PROBLEM</div>
          <p className="mt-2 text-sm leading-relaxed">{problem}</p>
        </div>
      )}
      {outcome && (
        <div className="bx p-4" style={{ background: "var(--paper-2)" }} data-testid="value-outcome">
          <div className="label-mono" style={{ color: "var(--accent)" }}>/ THE OUTCOME</div>
          <p className="mt-2 text-sm leading-relaxed">{outcome}</p>
        </div>
      )}
      {included.length > 0 && (
        <div className="bx p-4" style={{ background: "var(--paper-2)" }} data-testid="value-included">
          <div className="label-mono" style={{ color: "var(--accent)" }}>/ WHAT&rsquo;S INCLUDED</div>
          <ul className="mt-2 text-sm leading-relaxed list-disc pl-5 space-y-1">
            {included.map((it) => <li key={it}>{it}</li>)}
          </ul>
        </div>
      )}
      {audience && (
        <div className="bx p-4" style={{ background: "var(--paper-2)" }} data-testid="value-audience">
          <div className="label-mono" style={{ color: "var(--accent)" }}>/ WHO IT&rsquo;S FOR</div>
          <p className="mt-2 text-sm leading-relaxed">{audience}</p>
        </div>
      )}
      {different && (
        <div className="bx p-4" style={{ background: "var(--paper-2)" }} data-testid="value-different">
          <div className="label-mono" style={{ color: "var(--accent)" }}>/ WHY IT&rsquo;S DIFFERENT</div>
          <p className="mt-2 text-sm leading-relaxed">{different}</p>
        </div>
      )}
    </div>
  );
}
